/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:mta-learning":true});}catch(e){}}();import type { ContactMethod } from '@/lib/analytics/contact-links'

/** The payment types sent to GA4 for payment selection and purchase events. */
type Ga4PaymentType = 'credit_card' | 'invoice'

/** E-commerce event data. */
type EcommerceData = {
  currency: string
  value: number
  items: Array<Gtag.Item>
  transaction_id?: string
  tax?: number
  shipping?: number
  coupon?: string
  payment_type?: Ga4PaymentType
}

/** E-commerce event type for the `gtag` method. */
type DataLayerEcommerceEvent = {
  /** The name of the event, i.e. add_to_cart. */
  event: EcommerceEventName

  /** The e-commerce data to send with the event. */
  ecommerce: EcommerceData | null
}

/** Custom event type for the `gtag` method. */
type DataLayerCustomEvent = {
  event: CustomEventName
  [key: string]: unknown
}

/** The union of all event types. */
type DataLayerEvent = DataLayerEcommerceEvent | DataLayerCustomEvent
type DataLayer = Array<DataLayerEvent | IArguments | { event: 'gtm.js'; 'gtm.start': number }>

// Logger configuration
type LogLevel = 'debug' | 'info' | 'warn' | 'error'

interface LoggerConfig {
  enabled: boolean
  level: LogLevel
  prefix?: string
}

class EventLogger {
  private config: LoggerConfig

  constructor(config: LoggerConfig) {
    this.config = {
      prefix: '📊 Analytics:',
      ...config,
    }
  }

  private shouldLog(level: LogLevel): boolean {
    if (!this.config.enabled) return false

    const levels: LogLevel[] = ['debug', 'info', 'warn', 'error']
    return levels.indexOf(level) >= levels.indexOf(this.config.level)
  }

  debug(message: string, data?: unknown): void {
    if (this.shouldLog('debug')) {
      console.debug(`${this.config.prefix} ${message}`, data)
    }
  }

  info(message: string, data?: unknown): void {
    if (this.shouldLog('info')) {
      console.info(`${this.config.prefix} ${message}`, data)
    }
  }

  warn(message: string, data?: unknown): void {
    if (this.shouldLog('warn')) {
      console.warn(`${this.config.prefix} ${message}`, data)
    }
  }

  error(message: string, error?: unknown): void {
    if (this.shouldLog('error')) {
      console.error(`${this.config.prefix} ${message}`, error)
    }
  }
}

// Create logger instance
const logger = new EventLogger({
  enabled: process.env.NODE_ENV !== 'production',
  level: 'debug',
})

/**
 * The base parameters for all e-commerce events.
 */
interface BaseEcommerceParams {
  currency: string
  value: number
  items: Array<Gtag.Item>
}

interface AddToCartParams extends BaseEcommerceParams {}
interface RemoveFromCartParams extends BaseEcommerceParams {}
interface ViewCartParams extends BaseEcommerceParams {}
interface BeginCheckoutParams extends BaseEcommerceParams {}

/** Add shipping info event parameters. */
interface AddShippingInfoParams extends BaseEcommerceParams {
  /** The shipping tier selected (e.g., 'standard', 'express'). */
  shipping_tier?: string
}

/** Add payment info event parameters. */
interface AddPaymentInfoParams extends BaseEcommerceParams {
  /** The payment type selected. */
  payment_type?: Ga4PaymentType
}

/** Purchase event parameters. */
interface PurchaseParams extends BaseEcommerceParams {
  transaction_id: string
  tax?: number
  shipping?: number
  coupon?: string

  /** How the customer chose to pay, using the same values as add_payment_info. */
  payment_type?: Ga4PaymentType
}

/** Search event parameters. */
type FormSubmitParams = {
  /** The name of the form. */
  form_name: string
}

/** Contact link click parameters. */
type ContactClickParams = {
  /** The contact method of the link that was clicked. */
  contact_method: ContactMethod
}

/** E-commerce event name literal type. */
type EcommerceEventName =
  | 'add_to_cart'
  | 'remove_from_cart'
  | 'view_cart'
  | 'begin_checkout'
  | 'add_shipping_info'
  | 'add_payment_info'
  | 'purchase'

/** Custom event name literal type. */
type CustomEventName = 'form_submit' | 'contact_click'

/** The union of all event names. */
type GoogleTagManagerEvent = EcommerceEventName | CustomEventName

/** The parameter map for each event to payload. */
type EventParameterMap = {
  add_to_cart: AddToCartParams
  remove_from_cart: RemoveFromCartParams
  view_cart: ViewCartParams
  begin_checkout: BeginCheckoutParams
  add_shipping_info: AddShippingInfoParams
  add_payment_info: AddPaymentInfoParams
  purchase: PurchaseParams
  form_submit: FormSubmitParams
  contact_click: ContactClickParams
}

const isEcommerceEvent = (eventName: GoogleTagManagerEvent): eventName is EcommerceEventName => {
  const ecommerceEvents: Array<EcommerceEventName> = [
    'add_to_cart',
    'remove_from_cart',
    'view_cart',
    'begin_checkout',
    'add_shipping_info',
    'add_payment_info',
    'purchase',
  ]

  return ecommerceEvents.includes(eventName as EcommerceEventName)
}

declare global {
  interface Window {
    dataLayer: DataLayer
    gtag?: Gtag.Gtag
  }
}

/**
 * Sends a strictly typed event to Google Tag Manager and optionally to GA4 directly.
 *
 * @param eventName - The name of the event to send
 * @param parameters - Strictly typed parameters for the event
 */
const trackEvent = <T extends GoogleTagManagerEvent>(eventName: T, parameters: EventParameterMap[T]): void => {
  if (typeof window === 'undefined') {
    logger.warn('Window object not available. Event not tracked:', { eventName, parameters })
    return
  }

  try {
    logger.debug('Tracking event:', { eventName, parameters })
    window.dataLayer = window.dataLayer || []

    if (isEcommerceEvent(eventName)) {
      logger.debug('Processing e-commerce event')

      // Clear ecommerce object before sending new ecommerce event
      window.dataLayer.push({
        ecommerce: null,
      } as DataLayerEcommerceEvent)

      // Push the ecommerce event
      window.dataLayer.push({
        event: eventName,
        ecommerce: parameters,
      } as DataLayerEcommerceEvent)

      logger.info('E-commerce event tracked successfully', { eventName })
    } else {
      logger.debug('Processing custom event')

      // Push custom event
      window.dataLayer.push({
        event: eventName,
        ...parameters,
      } as DataLayerCustomEvent)

      logger.info('Custom event tracked successfully', { eventName })
    }
  } catch (error) {
    logger.error('Failed to track event:', error)
    throw error
  }
}

export {
  trackEvent,
  type GoogleTagManagerEvent,
  type EventParameterMap,
  type BaseEcommerceParams,
  type Ga4PaymentType,
  type DataLayer,
  type DataLayerEvent,
}
